import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    employees: '',
    visitors: '',
    operatingHours: '',
    operatingDays: '',
    machineType: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('employees', formData.employees);
      formBody.append('visitors', formData.visitors);
      formBody.append('operatingHours', formData.operatingHours);
      formBody.append('operatingDays', formData.operatingDays);
      formBody.append('machineType', formData.machineType.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d52p0k7b58v6mebe8aag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          employees: '',
          visitors: '',
          operatingHours: '',
          operatingDays: '',
          machineType: []
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckboxChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      machineType: prev.machineType.includes(value)
        ? prev.machineType.filter(item => item !== value)
        : [...prev.machineType, value]
    }));
  };

  const machineTypes = [
    'Snack Vending Machine',
    'Beverage Vending Machine',
    'Combo Vending Machine (Snacks & Drinks)',
    'Healthy Vending Machine',
    'Fresh Food Vending Machine',
    'Coffee Service',
    'Smart Vending Machine',
    'Custom Solution'
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-orange-100 px-5 py-2 rounded-full mb-4">
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              <i className="ri-alarm-warning-line text-orange-600 text-lg"></i>
            </div>
            <span className="text-sm font-bold text-orange-600 tracking-wide uppercase">
              Limited Availability - Apply Today
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            See If You Qualify
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Submit your information now to check eligibility for no-cost vending machine installation. Our team will respond within 24 hours with your qualification status.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm"
                    placeholder="(469) 922-3413"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="123 Main St, Little Elm, TX 75068"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="employees" className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Employees *
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={formData.employees}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option value="1-25">1-25 employees</option>
                    <option value="26-50">26-50 employees</option>
                    <option value="51-100">51-100 employees</option>
                    <option value="101-250">101-250 employees</option>
                    <option value="251-500">251-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="visitors" className="block text-sm font-semibold text-gray-700 mb-2">
                    Visitors Per Day *
                  </label>
                  <select
                    id="visitors"
                    name="visitors"
                    value={formData.visitors}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option value="0-50">0-50 visitors</option>
                    <option value="51-100">51-100 visitors</option>
                    <option value="101-250">101-250 visitors</option>
                    <option value="251-500">251-500 visitors</option>
                    <option value="500+">500+ visitors</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="operatingDays" className="block text-sm font-semibold text-gray-700 mb-2">
                  Days of Operation *
                </label>
                <select
                  id="operatingDays"
                  name="operatingDays"
                  value={formData.operatingDays}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select days</option>
                  <option value="Monday-Friday">Monday - Friday</option>
                  <option value="Monday-Saturday">Monday - Saturday</option>
                  <option value="7 Days a Week">7 Days a Week</option>
                  <option value="Custom Schedule">Custom Schedule</option>
                </select>
              </div>

              <div>
                <label htmlFor="operatingHours" className="block text-sm font-semibold text-gray-700 mb-2">
                  Hours of Operation *
                </label>
                <input
                  type="text"
                  id="operatingHours"
                  name="operatingHours"
                  value={formData.operatingHours}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="e.g., 8:00 AM - 6:00 PM"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Type of Vending Service * (Select all that apply)
                </label>
                <div className="space-y-3">
                  {machineTypes.map((type) => (
                    <label
                      key={type}
                      className="flex items-center cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        name="machineType"
                        value={type}
                        checked={formData.machineType.includes(type)}
                        onChange={() => handleCheckboxChange(type)}
                        className="hidden"
                      />
                      <div className={`w-5 h-5 flex items-center justify-center border-2 rounded transition-all mr-3 flex-shrink-0 ${
                        formData.machineType.includes(type)
                          ? 'bg-orange-500 border-orange-500'
                          : 'border-gray-300 group-hover:border-orange-400'
                      }`}>
                        {formData.machineType.includes(type) && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {type}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formData.machineType.length === 0}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Check My Eligibility'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">Thank You!</h3>
                  <p className="text-sm font-semibold text-green-800 mb-1">
                    Your information has been successfully submitted.
                  </p>
                  <p className="text-sm text-green-700">
                    We'll get back to you as soon as possible with your qualification status.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm font-semibold text-red-800">
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-1">Phone</div>
                    <a href="tel:+14699223413" className="text-base text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">
                      (469) 922-3413
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-1">Email</div>
                    <a href="mailto:vegoodiesllc@gmail.com" className="text-base text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">
                      vegoodiesllc@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-1">Service Area</div>
                    <p className="text-base text-gray-900">
                      Little Elm, Frisco, Carrollton<br />
                      Dallas-Fort Worth Metroplex, TX
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mr-4 flex-shrink-0">
                    <i className="ri-time-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-1">Business Hours</div>
                    <p className="text-base text-gray-900">
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 3:00 PM<br />
                      Sunday: Closed<br />
                      <span className="text-orange-600 font-semibold">Emergency Service: 24/7</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Why Choose VE Goodies?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">No-cost installation for qualified businesses</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">Zero upfront costs or hidden fees</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">Modern cashless payment systems</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">Smart vending technology with AI</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">Fresh healthy snacks and beverages</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm">24/7 emergency support available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
